import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2026-10-09 10:05:07 +0200 (Fri, 09 Oct 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.juenger $
//  Revision  : $Rev: 166412 $
//  Purpose   : Navigation Menu for presentations
// =============================================================================
// ---Third-party imports
import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Progress } from 'semantic-ui-react';
// ---Framework imports
import { Sel } from 'uix/registries';
import { VIcon, Link } from 'uix/components';
import { useSelector, useDelay } from 'uix/hooks';
import { WikiPane } from 'uix/wiki/render';
import { usePresLanguage } from '../hooks';
// ---Local imports
import S from './navigationMenu.css';
import { log } from 'uix/logging';
// ---Globals
const labelProps = (label)=>({
        title: label,
        'aria-label': label
    });
const hasHorizontalOverflow = (element)=>element.scrollWidth > element.clientWidth + 1;
const hasNavigationOverflow = (element)=>{
    if (hasHorizontalOverflow(element)) return true;
    let [left, center, right] = element.children;
    let sideWidth = Math.max(left?.scrollWidth || 0, right?.scrollWidth || 0);
    let columnGap = parseFloat(getComputedStyle(element).columnGap) || 0;
    let requiredWidth = (center?.scrollWidth || 0) + 2 * (sideWidth + columnGap);
    return requiredWidth > element.clientWidth + 1;
};
const useHorizontalOverflow = (enabled)=>{
    let ref = useRef(null);
    let [overflow, setOverflow] = useState(false);
    useEffect(()=>{
        let element = ref.current;
        if (!enabled || !element) {
            setOverflow(false);
            return;
        }
        let animationFrame;
        let resizeObserver;
        let mutationObserver;
        let observed = new Set();
        let disposed = false;
        let measure = ()=>{
            if (disposed) return;
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(()=>setOverflow(hasNavigationOverflow(element)));
        };
        let observeTree = ()=>{
            if (!resizeObserver) return;
            [
                element,
                ...element.querySelectorAll('*')
            ].forEach((node)=>{
                if (!observed.has(node)) {
                    observed.add(node);
                    resizeObserver.observe(node);
                }
            });
        };
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(measure);
            observeTree();
        }
        if (typeof MutationObserver !== 'undefined') {
            mutationObserver = new MutationObserver(()=>{
                observeTree();
                measure();
            });
            mutationObserver.observe(element, {
                childList: true,
                subtree: true,
                characterData: true
            });
        }
        window.addEventListener('resize', measure);
        document.fonts?.ready.then(measure);
        measure();
        return ()=>{
            disposed = true;
            cancelAnimationFrame(animationFrame);
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [
        enabled
    ]);
    return [
        overflow,
        ref
    ];
};
const LanguageSwitcher = ({ enableLang, idless })=>{
    let i18n = useSelector(Sel.i18n);
    let { presLang, selected, switchLanguage } = usePresLanguage(enableLang);
    if (!presLang || presLang.length <= 1) return null;
    let choices = presLang.map((lang)=>/*#__PURE__*/ _jsx("button", {
            type: "button",
            className: clsx(S.langButton, lang === selected && S.langButtonSelected),
            "aria-pressed": lang === selected,
            onClick: ()=>switchLanguage(lang),
            ...labelProps(i18n.get(`lang_${lang}`, lang.toUpperCase())),
            children: lang.toUpperCase()
        }, lang));
    return /*#__PURE__*/ _jsx("span", {
        id: idless ? undefined : 'languageSwitcher',
        className: S.langSwitcher,
        children: choices
    });
};
const HamburgerButton = /*#__PURE__*/ React.forwardRef(({ open, onClick, i18n, className }, ref)=>/*#__PURE__*/ _jsx("button", {
        ref: ref,
        type: "button",
        onClick: onClick,
        "aria-expanded": open,
        "aria-haspopup": "dialog",
        ...labelProps(open ? i18n.get('pres_a11y.closeMenu', 'Menü schließen') : i18n.get('pres_a11y.openMenu', 'Menü öffnen')),
        className: clsx(S.hamburger, open && S.open, className),
        children: /*#__PURE__*/ _jsxs("span", {
            className: S.lines,
            "aria-hidden": "true",
            children: [
                /*#__PURE__*/ _jsx("span", {}),
                /*#__PURE__*/ _jsx("span", {}),
                /*#__PURE__*/ _jsx("span", {})
            ]
        })
    }));
const NavEntry = ({ caption, active, onClick, meta, href })=>{
    let i18n = useSelector(Sel.i18n);
    let gotoLabel = i18n.get('pres_a11y.goto', {
        caption
    }, 'Gehe zu ${caption}');
    return /*#__PURE__*/ _jsxs("a", {
        href: href,
        style: {
            position: 'relative'
        },
        onClick: (event)=>{
            event.preventDefault();
            onClick(event);
        },
        "aria-current": active ? 'true' : undefined,
        ...labelProps(gotoLabel),
        className: clsx(active && S.active),
        children: [
            caption,
            /*#__PURE__*/ _jsx("div", {
                style: {
                    '--presentation_themeColor': meta?.themeColor,
                    background: 'var(--presentation_themeColor)'
                }
            })
        ]
    });
};
export const NavigationMenu = ({ mobile, menuContent, slides, activeSlide, setActiveSlide, progressPercent, progressAbsolute, logoPosition = 'left', parentPresId, rootNode, onOpenChange })=>{
    let apiurl = useSelector(Sel.apiurl);
    let i18n = useSelector(Sel.i18n);
    let [open, setOpen] = useState(false);
    let [closing, setClosing] = useState(false);
    let [disableTransition, setDisableTransition] = useState(false);
    // desktop overflow probe
    let [desktopOverflow, measureRowRef] = useHorizontalOverflow(!mobile);
    let navRef = useRef(null) // outer container (mobile)
    ;
    let triggerRef = useRef(null) // hamburger button
    ;
    let panelRef = useRef(null) // opened menu overlay
    ;
    // trigger focus restore
    let focusTriggerOnClose = useRef(false);
    let compact = mobile || desktopOverflow;
    let openMenu = ()=>{
        focusTriggerOnClose.current = false;
        setClosing(false);
        setOpen(true);
    };
    let closeMenu = ()=>{
        if (panelRef.current?.contains(document.activeElement)) focusTriggerOnClose.current = true;
        setOpen(false);
        setClosing(true);
    };
    let toggleMenu = ()=>open ? closeMenu() : openMenu();
    // delayed focus restore
    useDelay(()=>setClosing(false), closing ? 300 : null, [
        closing
    ]);
    useEffect(()=>{
        if (!closing && focusTriggerOnClose.current) {
            focusTriggerOnClose.current = false;
            triggerRef.current?.focus();
        }
    }, [
        closing
    ]);
    // move focus into the overlay on open (the panel itself is focusable via tabIndex -1)
    useEffect(()=>{
        if (open) panelRef.current?.focus();
    }, [
        open
    ]);
    // Close menu when clicking/tapping outside trigger and overlay, or on Escape
    useEffect(()=>{
        const handleOutside = (e)=>{
            if (!open) return;
            if (navRef.current?.contains(e.target)) return;
            if (triggerRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            closeMenu();
        };
        const handleKey = (e)=>{
            if (!open) return;
            if (e.key === 'Escape') closeMenu();
        };
        document.addEventListener('click', handleOutside, true);
        document.addEventListener('touchstart', handleOutside, true);
        document.addEventListener('keydown', handleKey);
        return ()=>{
            document.removeEventListener('click', handleOutside, true);
            document.removeEventListener('touchstart', handleOutside, true);
            document.removeEventListener('keydown', handleKey);
        };
    }, [
        open
    ]);
    // logo is first image webresource with a 'logo' in the tree node caption
    let logoAsset = rootNode?.assets?.find((e)=>e.datatype == 9 && e.subtype == 2 && e.caption?.toLowerCase().includes('logo'));
    let parentLogoAsset = rootNode?.assets?.find((e)=>e.datatype == 9 && e.subtype == 2 && e.caption?.toLowerCase().includes('home'));
    let styles = {};
    // scroll behavior for auto-hiding the navigation menu
    let [navVisible, setNavVisible] = useState(true);
    let [isHovered, setIsHovered] = useState(false);
    // y buffer on mouse-leave: pointer below the bar within the buffer still counts as hovering
    let HOVER_LEAVE_BUFFER = 80;
    let hoverLeaveCleanup = useRef(null);
    useEffect(()=>()=>hoverLeaveCleanup.current?.(), []);
    let setHovered = (h)=>{
        hoverLeaveCleanup.current?.();
        hoverLeaveCleanup.current = null;
        setIsHovered(h);
    };
    let onNavMouseLeave = (e)=>{
        let rect = navRef.current?.getBoundingClientRect();
        if (!rect || e.clientY < rect.bottom || e.clientY > rect.bottom + HOVER_LEAVE_BUFFER) {
            setHovered(false);
            return;
        }
        let onMove = (ev)=>{
            let r = navRef.current?.getBoundingClientRect();
            if (r && (ev.clientY > r.bottom + HOVER_LEAVE_BUFFER || ev.clientY < r.top)) setHovered(false);
        };
        hoverLeaveCleanup.current?.();
        hoverLeaveCleanup.current = ()=>document.removeEventListener('mousemove', onMove);
        document.addEventListener('mousemove', onMove);
    };
    useEffect(()=>{
        let val = compact || !!(open || navVisible || isHovered);
        onOpenChange?.(val);
    }, [
        compact,
        open,
        navVisible,
        onOpenChange,
        isHovered
    ]);
    let lastProgressRef = useRef(progressAbsolute);
    let isInitialJump = useRef(true);
    useEffect(()=>{
        // mode switch reset
        setOpen(false);
        setClosing(false);
        setDisableTransition(true);
        setNavVisible(true);
        let timer = setTimeout(()=>setDisableTransition(false), 600);
        return ()=>clearTimeout(timer);
    }, [
        compact
    ]);
    useEffect(()=>{
        let diff = progressAbsolute - lastProgressRef.current;
        // ignore the initial jump after mounting (when measuring slides causes a large progress change)
        if (isInitialJump.current && Math.abs(diff) > 0) {
            isInitialJump.current = false;
            lastProgressRef.current = progressAbsolute;
            return;
        }
        if (diff > 150) {
            // scrolling down fast: hide nav
            setNavVisible(false);
            lastProgressRef.current = progressAbsolute;
        } else if (diff < -50) {
            // scrolling up: show nav
            setNavVisible(true);
            lastProgressRef.current = progressAbsolute;
        } else if (progressAbsolute <= 100) {
            // at the very top: show nav
            setNavVisible(true);
            lastProgressRef.current = progressAbsolute;
        }
    }, [
        progressAbsolute
    ]);
    let navHidden = !compact && !(navVisible || open || isHovered);
    let navClasses = clsx(S.navigationContainer, navHidden && S.hideNav, disableTransition && S.noTransition);
    let menuItems;
    let rightMenuItems;
    let leftMenuItems;
    let hamburgerMenuItems;
    if (menuContent && menuContent.$children) {
        let { $children } = menuContent;
        let menuItemChildren = $children.filter((i)=>i.$tag === 'MenuItem');
        menuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position !== 'right' && i.position !== 'left' && i.position !== 'hamburger')
        };
        rightMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'right')
        };
        leftMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'left')
        };
        hamburgerMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'hamburger')
        };
    }
    // build room navigation from slides with isRoomSlide prop
    let roomNavEntries = slides.filter((slide)=>{
        let roomHideInMenu = slide.meta?.roomHideInMenu;
        const isHidden = roomHideInMenu !== undefined ? roomHideInMenu : slide.meta?.hideInMenu;
        /* hideInMenu old meta support */ return slide.isRoomSlide && !isHidden;
    }).map(({ caption, otid, index, meta })=>/*#__PURE__*/ _jsx(NavEntry, {
            style: {
                '--presentation_themeColor': meta.themeColor,
                background: 'var(--presentation_themeColor)'
            },
            meta: meta,
            caption: caption,
            href: `#${otid}`,
            active: slides[activeSlide]?.room?.otid == otid,
            onClick: ()=>setActiveSlide(index)
        }, otid));
    let backButton = parentPresId ? /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(Link, {
            className: clsx(S.backButton, parentLogoAsset && S.logo),
            id: parentPresId,
            ...labelProps(i18n.get('pres_a11y.backToExhibition', 'zurück zur Gesamtaustellung')),
            children: parentLogoAsset ? /*#__PURE__*/ _jsx("img", {
                alt: "",
                "aria-hidden": "true",
                src: apiurl.href(`/domainimage/${parentLogoAsset.otid}`)
            }) : /*#__PURE__*/ _jsx(VIcon, {
                name: "level up alternate",
                "aria-hidden": "true"
            })
        })
    }) : null;
    let logoLink = logoAsset ? /*#__PURE__*/ _jsx("a", {
        href: `#${slides[0]?.otid || ''}`,
        className: S.logo,
        ...labelProps(i18n.get('pres_a11y.toStart', 'Zum Anfang der Webaustellung')),
        onClick: (event)=>{
            event.preventDefault();
            setActiveSlide(0);
        },
        children: /*#__PURE__*/ _jsx("img", {
            alt: "",
            "aria-hidden": "true",
            src: apiurl.href(`/domainimage/${logoAsset.otid}`)
        })
    }) : null;
    let hasMenuItems = menuItems?.$children?.length > 0;
    let hasHamburgerItems = hamburgerMenuItems?.$children?.length > 0;
    let hasRightMenuItems = rightMenuItems?.$children?.length > 0;
    let hasRoomNav = roomNavEntries?.length > 0;
    let mobileMenuContent = hasMenuItems ? /*#__PURE__*/ _jsx(WikiPane, {
        rendered: menuItems
    }) : hasRoomNav ? roomNavEntries : hasHamburgerItems && /*#__PURE__*/ _jsx(WikiPane, {
        rendered: hamburgerMenuItems
    });
    let hamburgerVisible = compact ? !!mobileMenuContent || hasRightMenuItems : hasHamburgerItems;
    // persistent desktop probe
    let measureBar = /*#__PURE__*/ _jsx("div", {
        className: S.measureBar,
        "aria-hidden": "true",
        children: /*#__PURE__*/ _jsx("div", {
            className: S.navigation,
            children: /*#__PURE__*/ _jsxs("div", {
                className: S.navRow,
                ref: measureRowRef,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.nav, S.left),
                        children: [
                            backButton,
                            logoPosition == 'left' && logoLink,
                            leftMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                rendered: leftMenuItems
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("nav", {
                        className: clsx(S.nav),
                        children: [
                            logoPosition == 'center' && logoLink,
                            hasMenuItems ? /*#__PURE__*/ _jsx(WikiPane, {
                                rendered: menuItems
                            }) : hasRoomNav && roomNavEntries
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.nav, S.right),
                        children: [
                            hasRightMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                rendered: rightMenuItems
                            }),
                            logoPosition == 'right' && logoLink,
                            /*#__PURE__*/ _jsx(LanguageSwitcher, {
                                enableLang: rootNode.meta?.languages,
                                idless: true
                            })
                        ]
                    })
                ]
            })
        })
    });
    if (compact) {
        return /*#__PURE__*/ _jsxs("div", {
            ref: navRef,
            style: styles,
            className: navClasses,
            onMouseEnter: ()=>setHovered(true),
            onMouseLeave: onNavMouseLeave,
            children: [
                !mobile && measureBar,
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx('presNavigation', S.navigation, S.mobile),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: clsx(S.nav),
                            children: [
                                backButton,
                                logoLink,
                                leftMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                    rendered: leftMenuItems
                                }),
                                /*#__PURE__*/ _jsx(HamburgerButton, {
                                    ref: triggerRef,
                                    open: open,
                                    onClick: toggleMenu,
                                    i18n: i18n,
                                    className: !hamburgerVisible && S.hidden
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("nav", {
                            ref: panelRef,
                            role: "dialog",
                            "aria-label": i18n.get('pres_a11y.menu', 'Menü'),
                            tabIndex: -1,
                            onClick: closeMenu,
                            className: clsx(S.mobileMenu, open && S.open, S.nav),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: S.mainMenuContent,
                                    children: mobileMenuContent
                                }),
                                (hasMenuItems || hasRoomNav) && hasHamburgerItems && /*#__PURE__*/ _jsx("div", {
                                    className: S.hamburgerItems,
                                    children: /*#__PURE__*/ _jsx(WikiPane, {
                                        rendered: hamburgerMenuItems
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: S.bottom,
                                    children: [
                                        hasRightMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: rightMenuItems
                                        }),
                                        /*#__PURE__*/ _jsx(LanguageSwitcher, {
                                            enableLang: rootNode.meta?.languages
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Progress, {
                    className: clsx(S.progressBar, '.presProgressBar'),
                    percent: progressPercent,
                    attached: "top",
                    "aria-hidden": "true"
                })
            ]
        });
    } else {
        return /*#__PURE__*/ _jsxs("div", {
            style: styles,
            className: navClasses,
            onMouseEnter: ()=>setHovered(true),
            onMouseLeave: onNavMouseLeave,
            onFocus: ()=>setHovered(true),
            onBlur: (event)=>{
                if (!event.currentTarget.contains(event.relatedTarget)) setHovered(false);
            },
            children: [
                measureBar,
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx('presNavigation', S.navigation),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: S.navRow,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.nav, S.left),
                                    children: [
                                        backButton,
                                        logoPosition == 'left' && logoLink,
                                        leftMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: leftMenuItems
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("nav", {
                                    className: clsx(S.nav),
                                    children: [
                                        logoPosition == 'center' && logoLink,
                                        hasMenuItems ? /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: menuItems
                                        }) : hasRoomNav && roomNavEntries
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.nav, S.right),
                                    children: [
                                        hasRightMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: rightMenuItems
                                        }),
                                        logoPosition == 'right' && logoLink,
                                        /*#__PURE__*/ _jsx(LanguageSwitcher, {
                                            enableLang: rootNode.meta?.languages
                                        })
                                    ]
                                })
                            ]
                        }),
                        hasHamburgerItems && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.navigation),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: clsx(S.nav),
                                            children: /*#__PURE__*/ _jsx(HamburgerButton, {
                                                ref: triggerRef,
                                                open: open,
                                                onClick: toggleMenu,
                                                i18n: i18n
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Progress, {
                                            className: clsx(S.progressBar, '.presProgressBar'),
                                            percent: progressPercent,
                                            attached: "top",
                                            "aria-hidden": "true"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("nav", {
                                    ref: panelRef,
                                    role: "dialog",
                                    "aria-label": i18n.get('pres_a11y.menu', 'Menü'),
                                    tabIndex: -1,
                                    onClick: closeMenu,
                                    style: disableTransition ? {
                                        display: 'none'
                                    } : undefined,
                                    className: clsx(S.mobileMenu, open && S.open, S.nav),
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: S.hamburgerItems,
                                        children: /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: hamburgerMenuItems
                                        })
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Progress, {
                    className: S.progressBar,
                    percent: progressPercent,
                    attached: "top",
                    "aria-hidden": "true"
                })
            ]
        });
    }
};
